import React from 'react';
import { TextField, Box, Chip } from '@mui/material';
import { HighlightOff } from '@mui/icons-material';
import {
  StyledAutocomplete,
  StyledTagBox,
} from './Style/CustomAutocomplete.style';

export interface OptionType {
  id?: number | string;
  label: string;
  value?: string;
  disabled?: boolean;
}

type CustomAutocompleteProps = {
  items: OptionType[];
  selectedValues?: OptionType | OptionType[] | null;
  onChange: (event: any, value: OptionType | OptionType[] | null) => void;
  size?: 'small' | 'medium';
  placeholder?: string;
  label?: string;
  multiple?: boolean;
  dataTestid?: string;
  clearIcon?: React.ReactNode;
  showChips?: boolean;
  disableClearable?: boolean;
  capitalizeLabels?: boolean;
  limitTags?: number;
};

const CustomAutocomplete = ({
  items,
  selectedValues,
  size = 'medium',
  placeholder,
  label,
  onChange,
  multiple = false,
  dataTestid,
  clearIcon = false,
  showChips = true,
  disableClearable = false,
  capitalizeLabels = false,
  limitTags = -1,
}: CustomAutocompleteProps) => {
  const renderCommaSeparatedValues = (values: readonly OptionType[]) => {
    return values?.map(option => option?.label).join(', ');
  };

  const hasSelected = multiple
    ? Array.isArray(selectedValues) && selectedValues?.length > 0
    : selectedValues !== undefined && selectedValues !== null;

  return (
    <StyledAutocomplete
      multiple={multiple}
      limitTags={limitTags}
      data-testid={dataTestid}
      options={items}
      getOptionLabel={option => option?.label}
      disableCloseOnSelect
      freeSolo={false}
      onChange={onChange}
      value={selectedValues || (multiple ? [] : null)}
      isOptionEqualToValue={(option, value) => option?.label === value?.label}
      renderOption={(props, option) => {
        const isDisabled = option?.disabled;
        return (
          <Box
            component="li"
            {...props}
            key={`${option?.id}-${option?.label}`}
            sx={{
              textTransform: capitalizeLabels ? 'capitalize' : 'none',
              pointerEvents: isDisabled ? 'none' : 'auto',
              color: isDisabled ? 'text.disabled' : 'text.primary',
            }}>
            {option?.label}
          </Box>
        );
      }}
      renderTags={
        showChips
          ? (value: readonly OptionType[], getTagProps) =>
              value.map((option, index) => (
                <Box key={option.label}>
                  <Chip
                    label={option?.label}
                    {...getTagProps({ index })}
                    deleteIcon={<HighlightOff sx={{width: 13 , height: 13}} />}
                  />
                </Box>
              ))
          : (value: readonly OptionType[]) => (
              <StyledTagBox>{renderCommaSeparatedValues(value)}</StyledTagBox>
            )
      }
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={!hasSelected ? placeholder : ''}
          size={size}
          InputProps={{
            ...params.InputProps,
            endAdornment: null,
          }}
        />
      )}
      clearIcon={clearIcon}
      disableClearable={disableClearable as false}
    />
  );
};

export default CustomAutocomplete;
